// src/pages/attendance/AttendancePage.jsx

import React, { useState, useEffect, useContext, useMemo } from "react";
import { Clock, CheckCircle, XCircle, AlertCircle, Timer } from "lucide-react";
import { Mo3tiCard, Mo3tiStatCard } from "./components/mo3tiComponents";
import { useTheme } from "../theme";
import { LanguageContext } from "../LanguageContext";
import { apiFetch } from "../../context/AuthContext";
import { useProfile } from "../../hooks/useProfile";
import AlertModal from "../../components/AlertModal";

import { translations } from "./constant/translations";   
import { getAttendanceStyles } from "./styles/attendanceStyles";
import { useLocationState } from "./hooks/useLocationState";
import { useAttendanceData } from "./hooks/useAttendanceData";
import LocationDetailsPanel from "./components/LocationDetailsPanel";
import CheckInOutButtons from "./components/CheckInOutButtons";
import WFHModal from "./components/WFHModal";
import WeeklySection from "./components/WeeklySection";
import MonthlyTable from "./components/MonthlyTable";

const AttendancePage = ({ employeeData, attendanceData, onCheckIn, onCheckOut, currentUser }) => {
  const { currentTheme, isDarkMode } = useTheme();
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.ar;
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { profile } = useProfile();
  const infinityCode = profile?.infinityCode || localStorage.getItem("infinityCode");
  const employeeName = profile?.employeeName || localStorage.getItem("employeeName");

  const styles = getAttendanceStyles(currentTheme, isDarkMode, language);

  // ── local UI state ──────────────────────────────────────────────
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showWFHModal, setShowWFHModal] = useState(false);
  const [wfhDate, setWfhDate] = useState(new Date().toISOString().split("T")[0]);
  const [wfhReason, setWfhReason] = useState("");
  const [isSubmittingWFH, setIsSubmittingWFH] = useState(false);
  const [wfhStatus, setWfhStatus] = useState(null);
  const [hasApprovedWFHToday, setHasApprovedWFHToday] = useState(false);
  const [isCheckingWFH, setIsCheckingWFH] = useState(true);
  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });

  const showAlert = (title, message, variant = "info") =>
    setAlertModal({ open: true, title, message, variant });
  const closeAlert = () => setAlertModal((p) => ({ ...p, open: false }));

  // ── check WFH ───────────────────────────────────────────────────
  useEffect(() => {
    if (!infinityCode || !BASE_URL) { setIsCheckingWFH(false); return; }
    (async () => {
      try {
        setIsCheckingWFH(true);
        const today = new Date().toISOString().split("T")[0];
        const res = await apiFetch(`${BASE_URL}/api/leaves/wfh/check/${infinityCode}/${today}`, { method: "GET" });
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.success && data.hasApprovedWFH) {
          setHasApprovedWFHToday(true);
          setWfhStatus("approved");
        } else if (data.hasPendingWFH) {
          setWfhStatus("pending");
        }
      } catch { /* silent */ } finally { setIsCheckingWFH(false); }
    })();
  }, [infinityCode, BASE_URL]);

  // ── hooks ───────────────────────────────────────────────────────
  const { locationState, distance, isOutOfZone, refreshLocation } = useLocationState({
    hasApprovedWFHToday, isCheckingWFH, infinityCode, BASE_URL,
  });

  const {
    attendanceStats, loadingStats, weeklyData, monthlyAttendance,
    timeEntries, isLoadingTimeEntries,
    hasTodayCheckIn, todayCheckInDisplay,
    fetchTodayAttendanceRecord, fetchRecentTimeEntries,
  } = useAttendanceData({ currentUser, infinityCode, language, t, colors: currentTheme.colors });

  // ── clock ───────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ── check-in / check-out ────────────────────────────────────────
  const buildRecord = (status) => ({
    ipAddress: "127.0.0.1", portNo: 8000, protocol: "HTTP",
    macAddress: "web-interface", channelID: 1, activePostCount: 1,
    eventType: "AccessControllerEvent", eventState: "active",
    eventDescription: status === "checked-in"
      ? "Manual Check-in from Web Interface"
      : "Manual Check-out from Web Interface",
    deviceName: status === "checked-in" ? "Infinity Check-in" : "Infinity Check Out",
    majorEventType: 5, subEventType: 38,
    name: employeeName,
    cardReaderKind: 0, cardReaderNo: 1,
    verifyNo: Math.floor(Math.random() * 1000) + 200,
    employeeNoString: infinityCode,
    serialNo: Math.floor(Math.random() * 10000) + 14000,
    userType: "normal", currentVerifyMode: "manual",
    frontSerialNo: Math.floor(Math.random() * 10000) + 14000,
    attendanceStatus: status, label: "", statusValue: null,
    mask: "unknown", purePwdVerifyEnable: true,
  });

  const handleStartTimer = async () => {
    setIsProcessing(true);
    try {
      const rec = buildRecord("checked-in");
      const res = await apiFetch(`${BASE_URL}/fingerprint/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ AccessControllerEvent: rec, ...rec }),
      });
      if (!res.ok) throw new Error();
      setIsTimerRunning(true);
      await Promise.all([fetchTodayAttendanceRecord(), fetchRecentTimeEntries()]);
    } catch {
      showAlert(
        language === "ar" ? "خطأ" : "Error",
        language === "ar" ? "فشل تسجيل الحضور. يرجى المحاولة مرة أخرى." : "Check-in failed. Please try again.",
        "error"
      );
    } finally { setIsProcessing(false); }
  };

  const handleStopTimer = async () => {
    setIsProcessing(true);
    try {
      const rec = buildRecord("checked-out");
      const res = await apiFetch(`${BASE_URL}/fingerprint/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ AccessControllerEvent: rec, ...rec }),
      });
      if (!res.ok) throw new Error();
      setIsTimerRunning(false);
      await Promise.all([fetchTodayAttendanceRecord(), fetchRecentTimeEntries()]);
    } catch {
      showAlert(
        language === "ar" ? "خطأ" : "Error",
        language === "ar" ? "فشل تسجيل الانصراف. يرجى المحاولة مرة أخرى." : "Check-out failed. Please try again.",
        "error"
      );
    } finally { setIsProcessing(false); }
  };

  // ── WFH submit ──────────────────────────────────────────────────
  const handleSubmitWFH = async () => {
    if (!infinityCode || !String(infinityCode).trim()) {
      showAlert(language === "ar" ? "خطأ" : "Error",
        language === "ar" ? "رمز الموظف غير صالح." : "Invalid employee code.", "error");
      return;
    }
    if (!wfhReason.trim()) {
      showAlert(language === "ar" ? "تنبيه" : "Warning",
        language === "ar" ? "يرجى إدخال سبب الطلب" : "Please enter a reason.", "error");
      return;
    }
    setIsSubmittingWFH(true);
    try {
      const res = await apiFetch(`${BASE_URL}/api/leaves`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          infinityCode: String(infinityCode),
          leaveType: "wfh", startDate: wfhDate, endDate: wfhDate,
          reason: wfhReason,
          myAdmin: profile?.firstLineManagerInfinityCode || null,
          language,
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showAlert(language === "ar" ? "تم بنجاح" : "Success", t.workFromHome.success, "success");
      setShowWFHModal(false);
      setWfhReason("");
      setWfhStatus("pending");
    } catch (err) {
      showAlert(language === "ar" ? "خطأ" : "Error", t.workFromHome.error + ": " + err.message, "error");
    } finally { setIsSubmittingWFH(false); }
  };

  // ── format helpers (need language, kept here) ───────────────────
  const formatTime = (d) => d.toLocaleTimeString(language === "ar" ? "ar-EG" : "en-US", {
    hour12: true, hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const formatDate = (d) => d.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  // ── render ──────────────────────────────────────────────────────
  return (
    <div>
      <div style={styles.backgroundDecorative} />
      <div style={styles.contentWrapper}>

        {/* ── Time Tracker Card ── */}
        <div style={styles.card1({ screenSize: { isMobile: false, isTablet: false }, isRTL: language === "ar" })}>
        <div style={styles.timeTracker}>
          <div style={styles.timeTrackerDecoration} />
          <div style={styles.clockDisplay}>{formatTime(currentTime)}</div>
          <p style={styles.dateDisplay}>{formatDate(currentTime)}</p>

          <CheckInOutButtons
          
            isLoadingTimeEntries={isLoadingTimeEntries}
            isCheckingWFH={isCheckingWFH}
            isOutOfZone={isOutOfZone}
            hasApprovedWFHToday={hasApprovedWFHToday}
            locationState={locationState}
            isTimerRunning={isTimerRunning}
            hasTodayCheckIn={hasTodayCheckIn}
            isProcessing={isProcessing}
            handleStartTimer={handleStartTimer}
            handleStopTimer={handleStopTimer}
            setShowWFHModal={setShowWFHModal}
            wfhStatus={wfhStatus}
            t={t}
            currentTheme={currentTheme}
            isDarkMode={isDarkMode}
            language={language}
            todayCheckInDisplay={todayCheckInDisplay}

          />

          {/* Location panel / loading / error */}
          {!hasApprovedWFHToday && !locationState.isLoadingArea && !locationState.isLoadingUserLocation
            && locationState.userLocation && locationState.allowedArea && (
              <LocationDetailsPanel
                locationState={locationState}
                distance={distance}
                isOutOfZone={isOutOfZone}
                refreshLocation={refreshLocation}
                currentTheme={currentTheme}
                isDarkMode={isDarkMode}
                t={t}
              />
            )}

          {!hasApprovedWFHToday && (locationState.isLoadingArea || locationState.isLoadingUserLocation) && (
            <div style={{ padding: "20px", textAlign: "center", color: currentTheme.colors.textSecondary }}>
              <div style={styles.spinner} />
              <div style={{ fontSize: "14px", fontFamily: "'Cairo', sans-serif" }}>
                {locationState.isLoadingArea ? t.timeTracker.loadingLocation : t.timeTracker.gettingLocation}
              </div>
            </div>
          )}

          {!hasApprovedWFHToday && locationState.error
            && !locationState.isLoadingArea && !locationState.isLoadingUserLocation && (
                <div style={styles.locationErrorBadge}>
                  <div style={styles.locationErrorIcon}><AlertCircle size={24} color={currentTheme.colors.danger} strokeWidth={2} /></div>
                  <div>
                    <div style={styles.locationErrorTitle}>{t.timeTracker.locationError}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          ...styles.locationErrorSub,
                          color: currentTheme.colors.danger,
                          fontWeight: 700,
                          cursor: "pointer",
                         // textDecoration: "underline dotted",
                          userSelect: "none"
                        }}
                        onClick={refreshLocation}
                        tabIndex={0}
                        role="button"
                        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") refreshLocation(); }}
                      >
                        {language === "ar" ? "يرجى المحاولة مرة أخرى" : "Please try again"}
                      </span>
                    </div>
                  </div>
              </div>
            )}

          <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>
        </div>
        </div>
 {/* ── Stats Grid ── */}
        <div style={styles.statsGrid}>
          {loadingStats
            ? <div style={styles.loadingContainer}><p>{language === "ar" ? "جاري تحميل الإحصائيات..." : "Loading statistics..."}</p></div>
            : attendanceStats.map((stat, i) => (
              <Mo3tiStatCard key={i} icon={stat.icon} value={stat.value} label={stat.label}
                color={stat.color} trend={stat.trend} subtitle={stat.subtitle} theme={currentTheme} />
            ))
          }
        </div>
       

        {/* ── Weekly ── */}
        <WeeklySection
          weeklyData={weeklyData}
          t={t}
          currentTheme={currentTheme}
          isDarkMode={isDarkMode}
          language={language}
        />

        {/* ── Monthly + Recent ── */}
        <MonthlyTable
          monthlyAttendance={monthlyAttendance}
          timeEntries={timeEntries}
          isLoadingTimeEntries={isLoadingTimeEntries}
          t={t}
          currentTheme={currentTheme}
          isDarkMode={isDarkMode}
          language={language}
        />

      </div>

      {/* ── WFH Modal ── */}
      <WFHModal
        show={showWFHModal}
        onClose={() => setShowWFHModal(false)}
        wfhDate={wfhDate}
        setWfhDate={setWfhDate}
        wfhReason={wfhReason}
        setWfhReason={setWfhReason}
        isSubmittingWFH={isSubmittingWFH}
        onSubmit={handleSubmitWFH}
        t={t}
        currentTheme={currentTheme}
        isDarkMode={isDarkMode}
        language={language}
      />

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={isDarkMode ? "dark" : "light"}
        language={language}
      />
    </div>
  );
};

export default AttendancePage;